import { useState } from "react";
import {
  MessageSquare, Phone, Bot, ChevronDown, ChevronUp,
} from "lucide-react";

/**
 * ActivityTimeline — chat-bubble view of SMS + Call activities for a lead.
 *
 * Right-aligned outbound (blue), left-aligned inbound (white/emerald). Call
 * transcripts longer than 280 chars are collapsed with a toggle. AI summary
 * + intent (when available in `metadata`) renders as a yellow note below
 * the bubble.
 */
export default function ActivityTimeline({ activities, leadName }) {
  const [expanded, setExpanded] = useState({});
  const msgs = (activities || []).filter((a) => ["sms", "call"].includes(a.kind));

  if (msgs.length === 0) {
    return (
      <div className="px-5 py-10 text-center text-sm text-zinc-500" data-testid="conversation-empty">
        <MessageSquare className="w-6 h-6 mx-auto text-zinc-400 mb-2" />
        No SMS replies or call transcripts yet.
      </div>
    );
  }

  return (
    <div className="max-h-[520px] overflow-y-auto p-5 space-y-3 bg-zinc-50/50" data-testid="conversation-view">
      {msgs.map((a) => {
        const isInbound = a.direction === "inbound";
        const meta = a.metadata || {};
        const intent = meta.qualified_intent || meta.intent || meta.suggested_intent;
        const confidence = meta.qualified_confidence ?? meta.intent_confidence ?? meta.confidence;
        const summary = meta.transcript_summary || meta.summary || meta.ai_summary;
        const fullText = a.body || meta.transcript || meta.message || "";
        const isCall = a.kind === "call";
        const longText = fullText.length > 280;
        const isOpen = !!expanded[a.id];
        const displayed = isCall && longText && !isOpen ? fullText.slice(0, 280) + "…" : fullText;

        return (
          <div
            key={a.id}
            className={`flex ${isInbound ? "justify-start" : "justify-end"}`}
            data-testid={`conv-msg-${a.id}`}
          >
            <div className={`max-w-[80%] ${isInbound ? "" : "ml-auto"}`}>
              <div className={`flex items-center gap-1.5 mb-1 ${isInbound ? "" : "justify-end"}`}>
                <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                  isInbound ? "bg-emerald-600 text-white" : "bg-[#002FA7] text-white"
                }`}>
                  {isCall ? <Phone className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" /> : <MessageSquare className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />}
                  {isInbound ? `${leadName?.split(" ")[0] || "Lead"}` : "Outbound"}
                </span>
                <span className="font-mono text-[10px] text-zinc-500">
                  {new Date(a.created_at).toLocaleString()}
                </span>
              </div>

              <div
                className={`px-3 py-2.5 text-sm whitespace-pre-wrap leading-relaxed border ${
                  isInbound
                    ? "bg-white border-emerald-200 text-zinc-900 rounded-r-2xl rounded-tl-2xl"
                    : "bg-[#002FA7] border-[#002FA7] text-white rounded-l-2xl rounded-tr-2xl"
                }`}
                data-testid={`conv-bubble-${a.id}`}
              >
                {displayed || <em className="opacity-60">[empty]</em>}
                {isCall && longText && (
                  <button
                    type="button"
                    onClick={() => setExpanded((m) => ({ ...m, [a.id]: !m[a.id] }))}
                    data-testid={`conv-expand-${a.id}`}
                    className={`mt-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-0.5 ${
                      isInbound ? "text-emerald-700 hover:text-emerald-900" : "text-blue-200 hover:text-white"
                    }`}
                  >
                    {isOpen ? <><ChevronUp className="w-3 h-3" /> Collapse transcript</> : <><ChevronDown className="w-3 h-3" /> Show full transcript</>}
                  </button>
                )}
              </div>

              {(summary || intent) && (
                <div
                  className={`mt-1.5 px-2.5 py-1.5 border text-xs ${
                    isInbound ? "bg-amber-50 border-amber-200" : "bg-zinc-100 border-zinc-200"
                  }`}
                  data-testid={`conv-ai-${a.id}`}
                >
                  <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                    <Bot className="w-3 h-3 text-zinc-600" />
                    <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-600">
                      AI Analysis
                    </span>
                    {intent && (
                      <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                        intent === "interested" || intent === "positive"
                          ? "bg-emerald-100 text-emerald-800"
                          : intent === "not_interested" || intent === "negative"
                          ? "bg-red-100 text-red-800"
                          : intent === "qualified"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-zinc-200 text-zinc-700"
                      }`}>
                        {String(intent).replace(/_/g, " ")}
                        {typeof confidence === "number" && ` · ${Math.round(confidence * 100)}%`}
                      </span>
                    )}
                  </div>
                  {summary && <div className="text-zinc-700">{summary}</div>}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
